import { EXCEPTION_MODULE, isAlwaysOnModule, moduleOf } from '../catalog';
import type { AdminKey, JobKey } from '../catalog';
import { asLeafKey } from '../types';
import type { ExceptionKey, MeAccess, PlantAccess, PlantId } from '../types';

import { anyPlant, atPlant } from './rules';
import type { AccessRule, Scope } from './rules';

export type AllowedVia = 'GLOBAL_ADMIN' | 'ROLE' | 'OVERRIDE' | 'ADMIN_GRANT' | 'EXCEPTION' | 'SIGNED_IN' | 'UNVERIFIED';

export type DenyReason =
  | 'NO_ASSIGNMENT'
  | 'MODULE_UNLICENSED'
  | 'NOT_GRANTED'
  | 'NO_ADMIN_GRANT'
  | 'NO_PLANT_CONTEXT'
  | 'OUT_OF_CLUSTER'
  | 'AUDIENCE'
  | 'UNDECLARED';

export type Decision = { readonly allow: true; readonly via: AllowedVia } | { readonly allow: false; readonly reason: DenyReason };

export type PlantsWhere =
  | { readonly kind: 'all'; readonly except: readonly PlantId[] }
  | { readonly kind: 'some'; readonly plantIds: readonly PlantId[] };

const allowed = (via: AllowedVia): Decision => ({ allow: true, via });

const denied = (reason: DenyReason): Decision => ({ allow: false, reason });

const isGlobal = (me: MeAccess): boolean => me.adminGrant === 'GLOBAL';

const licensedAt = (plant: PlantAccess, module: string): boolean => isAlwaysOnModule(module) || plant.licensed.has(module);

const firstAllowed = (decisions: readonly Decision[]): Decision | undefined =>
  decisions.find((decision) => decision.allow && decision.via !== 'UNVERIFIED') ?? decisions.find((decision) => decision.allow);

const DENIAL_WEIGHT: Readonly<Record<DenyReason, number>> = {
  NOT_GRANTED: 0,
  MODULE_UNLICENSED: 1,
  OUT_OF_CLUSTER: 2,
  NO_ADMIN_GRANT: 3,
  NO_ASSIGNMENT: 4,
  NO_PLANT_CONTEXT: 5,
  AUDIENCE: 6,
  UNDECLARED: 7,
};

const mostTelling = (decisions: readonly Decision[], fallback: DenyReason): Decision =>
  decisions.reduce<Decision>(
    (best, decision) =>
      !decision.allow && !best.allow && DENIAL_WEIGHT[decision.reason] < DENIAL_WEIGHT[best.reason] ? decision : best,
    denied(fallback),
  );

const acrossPlants = (me: MeAccess, atOnePlant: (plantId: PlantId) => Decision): Decision => {
  if (me.plantIds.length === 0) return denied('NO_ASSIGNMENT');
  const decisions: Decision[] = [];
  for (const plantId of me.plantIds) {
    const decision = atOnePlant(plantId);
    if (decision.allow && decision.via !== 'UNVERIFIED') return decision;
    decisions.push(decision);
  }
  return firstAllowed(decisions) ?? mostTelling(decisions, 'NO_ASSIGNMENT');
};

const jobAt = (me: MeAccess, key: JobKey, plantId: PlantId): Decision => {
  const plant = me.plants.get(plantId);
  if (!plant) return denied('NO_ASSIGNMENT');
  if (!licensedAt(plant, moduleOf(key))) return denied('MODULE_UNLICENSED');
  const delta = plant.deltas.get(key);
  if (delta === 'revoke') return denied('NOT_GRANTED');
  if (delta === 'grant') return allowed('OVERRIDE');
  return me.baseKeys.has(key) ? allowed('ROLE') : denied('NOT_GRANTED');
};

const decideJob = (me: MeAccess, key: JobKey, scope: Scope): Decision => {
  if (isGlobal(me)) return allowed('GLOBAL_ADMIN');
  if (scope.kind === 'unresolved') return denied('NO_PLANT_CONTEXT');
  if (scope.kind === 'plant') return jobAt(me, key, scope.plantId);
  return acrossPlants(me, (plantId) => jobAt(me, key, plantId));
};

const adminAt = (me: MeAccess, key: AdminKey, plantId: PlantId): Decision => {
  const plant = me.plants.get(plantId);
  if (plant && !licensedAt(plant, moduleOf(key))) return denied('MODULE_UNLICENSED');
  if (me.clusterId === null && !me.isStaff) return denied('OUT_OF_CLUSTER');
  return plant && me.clusterId === null ? allowed('ADMIN_GRANT') : allowed('UNVERIFIED');
};

const decideAdmin = (me: MeAccess, key: AdminKey, scope: Scope): Decision => {
  if (isGlobal(me)) return allowed('GLOBAL_ADMIN');
  if (me.adminGrant === null) return denied('NO_ADMIN_GRANT');
  if (!me.adminKeys.has(asLeafKey(key))) return denied('NOT_GRANTED');
  if (scope.kind === 'unresolved') return denied('NO_PLANT_CONTEXT');
  return scope.kind === 'plant' ? adminAt(me, key, scope.plantId) : allowed('ADMIN_GRANT');
};

const exceptionAt = (me: MeAccess, key: ExceptionKey, plantId: PlantId): Decision => {
  const plant = me.plants.get(plantId);
  if (!plant) return isGlobal(me) ? allowed('UNVERIFIED') : denied('NO_ASSIGNMENT');
  if (!licensedAt(plant, EXCEPTION_MODULE[key])) return denied('MODULE_UNLICENSED');
  if (isGlobal(me)) return allowed('GLOBAL_ADMIN');
  return plant.exceptions.has(key) ? allowed('EXCEPTION') : denied('NOT_GRANTED');
};

const decideException = (me: MeAccess, key: ExceptionKey, scope: Scope): Decision => {
  if (scope.kind === 'unresolved') return denied('NO_PLANT_CONTEXT');
  if (scope.kind === 'plant') return exceptionAt(me, key, scope.plantId);
  if (isGlobal(me)) return firstAllowed(me.plantIds.map((plantId) => exceptionAt(me, key, plantId))) ?? allowed('UNVERIFIED');
  return acrossPlants(me, (plantId) => exceptionAt(me, key, plantId));
};

const decideAll = (me: MeAccess, rules: readonly AccessRule[], scope: Scope): Decision => {
  const decisions = rules.map((rule) => decide(me, rule, scope));
  const denial = decisions.find((decision) => !decision.allow);
  if (denial) return denial;
  return decisions.find((decision) => decision.allow && decision.via === 'UNVERIFIED') ?? decisions[0] ?? allowed('SIGNED_IN');
};

const decideAny = (me: MeAccess, rules: readonly AccessRule[], scope: Scope): Decision => {
  const decisions = rules.map((rule) => decide(me, rule, scope));
  return firstAllowed(decisions) ?? decisions[0] ?? denied('NOT_GRANTED');
};

export const decide = (me: MeAccess, rule: AccessRule, scope: Scope): Decision => {
  switch (rule.kind) {
    case 'signedIn':
      return allowed('SIGNED_IN');
    case 'job':
      return decideJob(me, rule.key, scope);
    case 'admin':
      return decideAdmin(me, rule.key, scope);
    case 'exception':
      return decideException(me, rule.key, scope);
    case 'internal':
      return me.isStaff ? decide(me, rule.rule, scope) : denied('AUDIENCE');
    case 'allOf':
      return decideAll(me, rule.rules, scope);
    case 'anyOf':
      return decideAny(me, rule.rules, scope);
    default: {
      const unhandled: never = rule;
      throw new Error(`Access rule without a decision: ${JSON.stringify(unhandled)}`);
    }
  }
};

export const includesPlant = (where: PlantsWhere, plantId: string): boolean =>
  where.kind === 'all' ? !where.except.some((id) => id === plantId) : where.plantIds.some((id) => id === plantId);

export const plantsWhere = (me: MeAccess, rule: AccessRule): PlantsWhere => {
  if (!decide(me, rule, anyPlant).allow) return { kind: 'some', plantIds: [] };
  if (isGlobal(me)) return { kind: 'all', except: me.plantIds.filter((plantId) => !decide(me, rule, atPlant(plantId)).allow) };
  return { kind: 'some', plantIds: me.plantIds.filter((plantId) => decide(me, rule, atPlant(plantId)).allow) };
};
